import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Spin,
  Alert,
  Button,
  Empty,
  Space,
  Input,
  Badge,
  Tooltip,
  Statistic,
  message,
  Select,
  Divider,
  Progress,
  Switch,
  TimePicker,
  Checkbox,
  Slider,
  Collapse,
  Modal,
} from 'antd';
import {
  DatabaseOutlined,
  TableOutlined,
  SearchOutlined,
  ReloadOutlined,
  ColumnHeightOutlined,
  FolderOpenOutlined,
  SyncOutlined,
  CloudDownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  ScheduleOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getCachedDatabaseCatalog,
  getCachedViewColumns,
  scanAndCacheDatabase,
  getScanStatus,
  getCacheInfo,
  getScheduleConfig,
  setScheduleConfig,
  resetCatalogCache,
} from '../services/dataService';
import useDataPermissions from '../hooks/useDataPermissions';

const { Title, Text } = Typography;
const { Search } = Input;
const { Panel } = Collapse;

function DataExplorer() {
  // Catalog browser state
  const [loading, setLoading] = useState(false);
  const [loadingView, setLoadingView] = useState(false);
  const [catalogData, setCatalogData] = useState(null);
  const { isDenied: isAccessDenied } = useDataPermissions();
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [selectedView, setSelectedView] = useState(null);
  const [viewDetails, setViewDetails] = useState({});
  const [error, setError] = useState(null);
  const [schemaSearch, setSchemaSearch] = useState('');
  const [viewSearch, setViewSearch] = useState('');
  const [columnSearch, setColumnSearch] = useState('');
  const [cacheInfo, setCacheInfo] = useState(null);

  // Sync state
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const pollRef = useRef(null);

  // Sync config state
  const [syncLayers, setSyncLayers] = useState(['REFINED_', 'RAW_', 'ANALYTIC_']);
  const [syncAllLayers, setSyncAllLayers] = useState(true);
  const [maxWorkers, setMaxWorkers] = useState(3);

  // Schedule state
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const DATA_LAYERS = [
    { value: 'REFINED_', label: 'REFINED', color: '#1890ff' },
    { value: 'RAW_', label: 'RAW', color: '#faad14' },
    { value: 'ANALYTIC_', label: 'ANALYTIC', color: '#52c41a' },
  ];

  // ── Load data on mount ──────────────────────────────────────────
  useEffect(() => {
    loadCatalog();
    loadCacheInfo();
    loadSchedule();
    checkRunningStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCacheInfo = useCallback(async () => {
    try {
      const info = await getCacheInfo();
      setCacheInfo(info);
    } catch (err) {
      console.warn('Could not load cache info:', String(err?.message || err).replace(/[\r\n]/g, ' '));
    }
  }, []);

  const loadCatalog = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCachedDatabaseCatalog();
      if (!data.cached) {
        setCatalogData(null);
        return;
      }
      setCatalogData(data);
    } catch (err) {
      console.error('Failed to load catalog:', String(err?.message || err).replace(/[\r\n]/g, ' '));
      setError('Failed to load catalog data: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const loadSchedule = async () => {
    try {
      const cfg = await getScheduleConfig();
      setSchedule(cfg);
    } catch (err) {
      console.warn('Could not load schedule:', String(err?.message || err).replace(/[\r\n]/g, ' '));
    }
  };

  const checkRunningStatus = async () => {
    try {
      const status = await getScanStatus();
      if (status.is_running || status.status === 'scanning') {
        setScanning(true);
        setScanProgress(status);
        startPolling();
      } else if (status.status === 'completed' || status.status === 'failed') {
        setScanProgress(status);
      }
    } catch (err) {
      // ignore
    }
  };

  // ── Polling ─────────────────────────────────────────────────────
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const status = await getScanStatus();
        setScanProgress(status);

        if (status.status === 'completed') {
          stopPolling();
          setScanning(false);
          message.success(
            `Sync complete! ${status.result?.total_schemas || 0} schemas, ${status.result?.total_views || 0} views, ${status.result?.total_columns || 0} columns.`
          );
          await loadCatalog();
          await loadCacheInfo();
        } else if (status.status === 'failed') {
          stopPolling();
          setScanning(false);
          message.error(`Sync failed: ${status.message}`);
        }
      } catch (err) {
        console.error('Poll error:', String(err?.message || err).replace(/[\r\n]/g, ' '));
      }
    }, 2000);
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // ── Sync actions ────────────────────────────────────────────────
  const handleStartSync = async () => {
    setScanning(true);
    setScanProgress(null);
    try {
      const layers = syncAllLayers ? null : syncLayers;
      const resp = await scanAndCacheDatabase({ data_layers: layers, max_workers: maxWorkers });
      if (resp.status === 'already_running') {
        message.warning('A sync is already in progress.');
      } else {
        message.success('Catalog sync started.');
      }
      startPolling();
    } catch (err) {
      setScanning(false);
      message.error('Failed to start sync: ' + (err.message || 'Unknown error'));
    }
  };

  // ── Schedule actions ────────────────────────────────────────────
  const handleSaveSchedule = async (updates) => {
    setScheduleLoading(true);
    try {
      const current = schedule || { enabled: false, frequency: 'daily', hour: 2, minute: 0, data_layers: null, max_workers: 3 };
      const newCfg = { ...current, ...updates };
      const resp = await setScheduleConfig(newCfg);
      setSchedule(resp.config || newCfg);
      message.success('Schedule saved.');
    } catch (err) {
      message.error('Failed to save schedule.');
    } finally {
      setScheduleLoading(false);
    }
  };

  // ── View detail loading ─────────────────────────────────────────
  const handleResetCache = () => {
    Modal.confirm({
      title: 'Reset Catalog Cache',
      content: 'This will delete all cached catalog data (schemas, views, columns). You will need to run a new sync to repopulate. Continue?',
      okText: 'Reset Cache',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await resetCatalogCache();
          setCatalogData(null);
          setCacheInfo(null);
          setSelectedSchema(null);
          setSelectedView(null);
          setViewDetails({});
          setScanProgress(null);
          message.success('Catalog cache has been cleared');
          await loadCacheInfo();
        } catch (err) {
          message.error('Failed to reset cache: ' + (err.message || 'Unknown error'));
        }
      },
    });
  };

  const loadViewDetails = async (schema, viewName) => {
    const viewKey = `${schema}.${viewName}`;
    if (viewDetails[viewKey]) {
      setSelectedView({ schema, viewName, key: viewKey });
      return;
    }
    setLoadingView(true);
    try {
      const data = await getCachedViewColumns(schema, viewName);
      if (data.cached && data.view) {
        setViewDetails(prev => ({ ...prev, [viewKey]: data.view }));
        setSelectedView({ schema, viewName, key: viewKey });
      } else {
        message.info(`Column details not cached for ${viewKey}. Run sync first.`);
      }
    } catch (err) {
      message.error('Failed to load view details.');
    } finally {
      setLoadingView(false);
    }
  };

  // ── Derived data ────────────────────────────────────────────────
  const getFilteredSchemas = () => {
    if (!catalogData?.schemas) return [];
    return catalogData.schemas.filter(s => {
      if (!schemaSearch) return true;
      const q = schemaSearch.toLowerCase();
      return s.SCHEMA?.toLowerCase().includes(q) || s.DESC?.toLowerCase().includes(q);
    });
  };

  const getSchemaViews = (schema) => {
    if (!schema?.VIEWS) return [];
    return schema.VIEWS.split(',').map(v => v.trim()).sort();
  };

  const getFilteredViews = () => {
    if (!selectedSchema) return [];
    const views = getSchemaViews(selectedSchema);
    if (!viewSearch) return views;
    const q = viewSearch.toLowerCase();
    return views.filter(v => v.toLowerCase().includes(q));
  };

  const getFilteredColumns = () => {
    if (!selectedView || !viewDetails[selectedView.key]) return [];
    const cols = viewDetails[selectedView.key].COLUMNS || [];
    if (!columnSearch) return cols;
    const q = columnSearch.toLowerCase();
    return cols.filter(c =>
      c.COLUMN_NAME?.toLowerCase().includes(q) ||
      c.TYPE?.toLowerCase().includes(q) ||
      c.COLUMN_DESCRIPTION?.toLowerCase().includes(q)
    );
  };

  const getStats = () => {
    if (!catalogData?.schemas) return { totalSchemas: 0, totalViews: 0, layers: {} };
    const stats = { totalSchemas: catalogData.schemas.length, totalViews: 0, layers: {} };
    catalogData.schemas.forEach(s => {
      const views = s.VIEWS ? s.VIEWS.split(',').length : 0;
      stats.totalViews += views;
      const name = s.SCHEMA.toUpperCase();
      let layer = 'OTHER';
      if (name.startsWith('ANALYTIC')) layer = 'ANALYTIC';
      else if (name.startsWith('REFINED')) layer = 'REFINED';
      else if (name.startsWith('RAW')) layer = 'RAW';
      stats.layers[layer] = (stats.layers[layer] || 0) + 1;
    });
    return stats;
  };

  // ── Formatting helpers ──────────────────────────────────────────
  const formatTimeAgo = (isoStr) => {
    if (!isoStr) return 'Unknown';
    try {
      const d = new Date(isoStr);
      const diffMs = Date.now() - d.getTime();
      const mins = Math.floor(diffMs / 60000);
      const hrs = Math.floor(diffMs / 3600000);
      const days = Math.floor(diffMs / 86400000);
      if (mins < 1) return 'Just now';
      if (mins < 60) return `${mins}m ago`;
      if (hrs < 24) return `${hrs}h ago`;
      if (days < 7) return `${days}d ago`;
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return isoStr;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getSchemaColor = (name) => {
    const n = name.toUpperCase();
    if (n.startsWith('ANALYTIC')) return '#52c41a';
    if (n.startsWith('REFINED')) return '#1890ff';
    if (n.startsWith('RAW')) return '#faad14';
    return '#8c8c8c';
  };

  const getSchemaLayer = (name) => {
    const n = name.toUpperCase();
    if (n.startsWith('ANALYTIC')) return 'ANALYTIC';
    if (n.startsWith('REFINED')) return 'REFINED';
    if (n.startsWith('RAW')) return 'RAW';
    return 'OTHER';
  };

  const filteredSchemas = getFilteredSchemas();
  const filteredViews = getFilteredViews();
  const filteredColumns = getFilteredColumns();
  const stats = getStats();

  // ── Render ──────────────────────────────────────────────────────
  return (
    <div className="page-container">
      {/* ── Page Header ─────────────────────────────────────────── */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ color: 'var(--theme-primary)', margin: 0 }}>
            <SyncOutlined style={{ marginRight: 8 }} />
            Data Catalog Sync
          </Title>
          <Text type="secondary">Synchronize and browse Snowflake metadata</Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<DeleteOutlined />}
              onClick={handleResetCache}
              disabled={!cacheInfo?.database?.cached || scanning}
              danger
            >
              Reset Cache
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => { loadCatalog(); loadCacheInfo(); loadSchedule(); }} loading={loading}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      {/* ── Sync Control + Status Row ───────────────────────────── */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {/* Sync Configuration Card */}
        <Col xs={24} lg={14}>
          <Card
            title={<Space><ThunderboltOutlined style={{ color: '#722ed1' }} /><span>Sync Configuration</span></Space>}
            size="small"
            style={{ height: '100%' }}
          >
            {/* Data Layers */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Data Layers</Text>
              <Space direction="vertical" size={4}>
                <Checkbox
                  checked={syncAllLayers}
                  onChange={(e) => {
                    setSyncAllLayers(e.target.checked);
                    if (e.target.checked) setSyncLayers(['REFINED_', 'RAW_', 'ANALYTIC_']);
                  }}
                >
                  <Text>All Layers</Text>
                </Checkbox>
                {!syncAllLayers && (
                  <Checkbox.Group
                    value={syncLayers}
                    onChange={(vals) => setSyncLayers(vals)}
                    style={{ marginLeft: 24 }}
                  >
                    <Space>
                      {DATA_LAYERS.map(l => (
                        <Checkbox key={l.value} value={l.value}>
                          <Tag color={l.color}>{l.label}</Tag>
                        </Checkbox>
                      ))}
                    </Space>
                  </Checkbox.Group>
                )}
              </Space>
            </div>

            {/* Concurrency */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                Concurrency: {maxWorkers} thread{maxWorkers > 1 ? 's' : ''}
              </Text>
              <Slider
                min={1}
                max={8}
                value={maxWorkers}
                onChange={setMaxWorkers}
                marks={{ 1: '1', 3: '3', 5: '5', 8: '8' }}
                style={{ maxWidth: 300 }}
              />
            </div>

            {/* Start Button */}
            <Button
              type="primary"
              icon={scanning ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={handleStartSync}
              loading={scanning}
              size="large"
              disabled={!syncAllLayers && syncLayers.length === 0}
              style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
            >
              {scanning ? 'Syncing...' : 'Start Sync'}
            </Button>
          </Card>
        </Col>

        {/* Sync Status Card */}
        <Col xs={24} lg={10}>
          <Card
            title={<Space><SyncOutlined spin={scanning} style={{ color: scanning ? '#722ed1' : '#8c8c8c' }} /><span>Sync Status</span></Space>}
            size="small"
            style={{ height: '100%' }}
          >
            {/* Progress bar */}
            {scanProgress && (scanProgress.status === 'scanning' || scanProgress.status === 'completed' || scanProgress.status === 'failed') ? (
              <div>
                <Progress
                  percent={Math.round(scanProgress.progress || 0)}
                  status={
                    scanProgress.status === 'completed' ? 'success'
                    : scanProgress.status === 'failed' ? 'exception'
                    : 'active'
                  }
                  strokeColor={scanProgress.status === 'scanning' ? '#722ed1' : undefined}
                  style={{ marginBottom: 12 }}
                />

                {/* Status Message */}
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
                  {scanProgress.message}
                </Text>

                {/* Timing row */}
                <Row gutter={16} style={{ marginBottom: 8 }}>
                  <Col span={8}>
                    <Statistic
                      title="Elapsed"
                      value={formatDuration(scanProgress.elapsed_seconds)}
                      valueStyle={{ fontSize: 14 }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="ETA"
                      value={scanProgress.eta_seconds != null ? formatDuration(scanProgress.eta_seconds) : '--'}
                      valueStyle={{ fontSize: 14 }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Batches"
                      value={`${scanProgress.completed_batches || 0} / ${scanProgress.total_batches || 0}`}
                      valueStyle={{ fontSize: 14, color: scanProgress.failed_batches > 0 ? '#ff4d4f' : undefined }}
                      suffix={scanProgress.failed_batches > 0 ? <Text type="danger" style={{ fontSize: 11 }}>({scanProgress.failed_batches} failed)</Text> : null}
                    />
                  </Col>
                </Row>

                {/* Data layers badge */}
                {scanProgress.data_layers && (
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>Layers: </Text>
                    {scanProgress.data_layers.map(l => (
                      <Tag key={l} color="purple" style={{ fontSize: 10 }}>{l.replace('_', '')}</Tag>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <InfoCircleOutlined style={{ fontSize: 32, color: '#d9d9d9', marginBottom: 8 }} />
                <br />
                <Text type="secondary">No sync in progress</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ── Cache Overview + Schedule Row ───────────────────────── */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {/* Cache Overview */}
        <Col xs={24} lg={14}>
          <Card
            title={
              <Space>
                <CloudDownloadOutlined style={{ color: cacheInfo?.database?.cached ? '#52c41a' : '#faad14' }} />
                <span>Cache Overview</span>
                {cacheInfo?.database?.cached && (
                  <Tag color="green" style={{ fontSize: 10 }}>Synced</Tag>
                )}
              </Space>
            }
            size="small"
          >
            {cacheInfo?.database?.cached ? (
              <Row gutter={[24, 12]}>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Schemas"
                    value={stats.totalSchemas}
                    prefix={<DatabaseOutlined />}
                    valueStyle={{ fontSize: 18, color: 'var(--theme-primary)' }}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Views"
                    value={stats.totalViews}
                    prefix={<TableOutlined />}
                    valueStyle={{ fontSize: 18, color: '#1890ff' }}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Columns"
                    value={cacheInfo.views?.column_count || 0}
                    prefix={<ColumnHeightOutlined />}
                    valueStyle={{ fontSize: 18, color: '#52c41a' }}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Cache Size"
                    value={formatFileSize(
                      (cacheInfo.database?.file_size_bytes || 0) +
                      (cacheInfo.schemas?.file_size_bytes || 0) +
                      (cacheInfo.views?.file_size_bytes || 0)
                    )}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Col>
                <Col span={24}>
                  <Divider style={{ margin: '8px 0' }} />
                  <Space split={<Divider type="vertical" />}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <ClockCircleOutlined /> Last synced: <Text strong style={{ fontSize: 12 }}>{formatTimeAgo(cacheInfo.database?.cached_at)}</Text>
                    </Text>
                    {Object.entries(stats.layers).map(([layer, count]) => (
                      <Text key={layer} type="secondary" style={{ fontSize: 12 }}>
                        <Tag
                          color={layer === 'REFINED' ? 'blue' : layer === 'RAW' ? 'orange' : layer === 'ANALYTIC' ? 'green' : 'default'}
                          style={{ fontSize: 10 }}
                        >
                          {layer}
                        </Tag>
                        {count} schemas
                      </Text>
                    ))}
                  </Space>
                </Col>
              </Row>
            ) : (
              <Empty
                image={<DatabaseOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
                imageStyle={{ height: 48 }}
                description={
                  <Text type="secondary">No cached data. Run a sync to populate the catalog.</Text>
                }
              />
            )}
          </Card>
        </Col>

        {/* Schedule Config */}
        <Col xs={24} lg={10}>
          <Card
            title={<Space><ScheduleOutlined style={{ color: '#1890ff' }} /><span>Automatic Sync Schedule</span></Space>}
            size="small"
            loading={scheduleLoading}
            style={{ height: '100%' }}
          >
            {schedule ? (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Row align="middle" gutter={12}>
                  <Col>
                    <Text strong>Enabled</Text>
                  </Col>
                  <Col>
                    <Switch
                      checked={schedule.enabled}
                      onChange={(checked) => handleSaveSchedule({ enabled: checked })}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                    />
                  </Col>
                  {schedule.enabled && (
                    <Col>
                      <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>
                    </Col>
                  )}
                </Row>

                <Row gutter={12} align="middle">
                  <Col>
                    <Text style={{ fontSize: 13 }}>Frequency:</Text>
                  </Col>
                  <Col>
                    <Select
                      value={schedule.frequency}
                      onChange={(val) => handleSaveSchedule({ frequency: val })}
                      size="small"
                      style={{ width: 100 }}
                    >
                      <Select.Option value="daily">Daily</Select.Option>
                      <Select.Option value="weekly">Weekly</Select.Option>
                    </Select>
                  </Col>
                  <Col>
                    <Text style={{ fontSize: 13 }}>at</Text>
                  </Col>
                  <Col>
                    <TimePicker
                      value={dayjs().hour(schedule.hour).minute(schedule.minute)}
                      format="HH:mm"
                      size="small"
                      onChange={(time) => {
                        if (time) handleSaveSchedule({ hour: time.hour(), minute: time.minute() });
                      }}
                      style={{ width: 90 }}
                    />
                  </Col>
                  <Col>
                    <Text type="secondary" style={{ fontSize: 11 }}>UTC</Text>
                  </Col>
                </Row>

                {schedule.data_layers && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>Layers: </Text>
                    {schedule.data_layers.map(l => (
                      <Tag key={l} style={{ fontSize: 10 }}>{l.replace('_', '')}</Tag>
                    ))}
                  </div>
                )}
              </Space>
            ) : (
              <Spin size="small" />
            )}
          </Card>
        </Col>
      </Row>

      {/* ── Error Alert ─────────────────────────────────────────── */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── Catalog Browser (collapsible) ───────────────────────── */}
      <Collapse
        defaultActiveKey={catalogData ? ['catalog'] : []}
        style={{ marginBottom: 16 }}
      >
        <Panel
          key="catalog"
          header={
            <Space>
              <DatabaseOutlined style={{ color: 'var(--theme-primary)' }} />
              <Text strong>Catalog Browser</Text>
              {catalogData && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ({stats.totalSchemas} schemas, {stats.totalViews} views)
                </Text>
              )}
            </Space>
          }
        >
          {loading && !catalogData ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" tip="Loading cached catalog..." />
            </div>
          ) : !catalogData || !catalogData.schemas || catalogData.schemas.length === 0 ? (
            <Empty
              image={<DatabaseOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />}
              description={
                <Space direction="vertical" size={4}>
                  <Text strong>No cached catalog data</Text>
                  <Text type="secondary">
                    Use the Sync Configuration above to scan the database.
                  </Text>
                </Space>
              }
            />
          ) : (
            <Row gutter={16}>
              {/* Column 1: Schemas */}
              <Col xs={24} lg={8}>
                <Card
                  title={
                    <Space>
                      <DatabaseOutlined style={{ color: 'var(--theme-primary)' }} />
                      <span>Schemas</span>
                      <Badge count={filteredSchemas.length} style={{ backgroundColor: 'var(--theme-primary)' }} />
                    </Space>
                  }
                  extra={
                    <Search
                      placeholder="Search..."
                      size="small"
                      allowClear
                      value={schemaSearch}
                      onChange={(e) => setSchemaSearch(e.target.value)}
                      style={{ width: 160 }}
                    />
                  }
                  size="small"
                  style={{ height: 'calc(100vh - 460px)', minHeight: 300 }}
                  bodyStyle={{ height: 'calc(100% - 49px)', overflow: 'auto', padding: 0 }}
                >
                  {filteredSchemas.length === 0 ? (
                    <Empty description="No schemas found" style={{ padding: '40px 0' }} />
                  ) : (
                    filteredSchemas.map((schema) => {
                      const views = getSchemaViews(schema);
                      const color = getSchemaColor(schema.SCHEMA);
                      const layer = getSchemaLayer(schema.SCHEMA);
                      const isSelected = selectedSchema?.SCHEMA === schema.SCHEMA;
                      const denied = !!schema.access_denied || isAccessDenied(schema.SCHEMA);

                      return (
                        <div
                          key={schema.SCHEMA}
                          onClick={() => {
                            if (denied) return;
                            setSelectedSchema(schema); setSelectedView(null);
                          }}
                          title={denied ? 'Access denied by administrator' : undefined}
                          style={{
                            padding: '10px 14px',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: denied ? 'not-allowed' : 'pointer',
                            opacity: denied ? 0.45 : 1,
                            backgroundColor: isSelected ? '#e6f7ff' : (denied ? '#fafafa' : 'transparent'),
                            borderLeft: isSelected ? `4px solid ${color}` : '4px solid transparent',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => { if (!isSelected && !denied) e.currentTarget.style.backgroundColor = '#fafafa'; }}
                          onMouseLeave={(e) => { if (!isSelected && !denied) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Space>
                              <FolderOpenOutlined style={{ color, fontSize: 14 }} />
                              <Text strong style={{ color, fontSize: 13 }}>{schema.SCHEMA}</Text>
                              <Tag color={color} style={{ fontSize: 10 }}>{layer}</Tag>
                              <Badge count={views.length} style={{ backgroundColor: color }} />
                              {denied && <Tag color="red" style={{ fontSize: 10 }}>No access</Tag>}
                            </Space>
                            {schema.DESC && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {schema.DESC.length > 80 ? schema.DESC.substring(0, 80) + '...' : schema.DESC}
                              </Text>
                            )}
                          </Space>
                        </div>
                      );
                    })
                  )}
                </Card>
              </Col>

              {/* Column 2: Views */}
              <Col xs={24} lg={8}>
                <Card
                  title={
                    <Space>
                      <TableOutlined style={{ color: '#1890ff' }} />
                      <span>Views</span>
                      {selectedSchema && <Badge count={filteredViews.length} style={{ backgroundColor: '#1890ff' }} />}
                    </Space>
                  }
                  extra={
                    selectedSchema && (
                      <Search
                        placeholder="Search..."
                        size="small"
                        allowClear
                        value={viewSearch}
                        onChange={(e) => setViewSearch(e.target.value)}
                        style={{ width: 160 }}
                      />
                    )
                  }
                  size="small"
                  style={{ height: 'calc(100vh - 460px)', minHeight: 300 }}
                  bodyStyle={{ height: 'calc(100% - 49px)', overflow: 'auto', padding: 0 }}
                >
                  {!selectedSchema ? (
                    <Empty description="Select a schema" style={{ padding: '40px 0' }} />
                  ) : filteredViews.length === 0 ? (
                    <Empty description="No views found" style={{ padding: '40px 0' }} />
                  ) : (
                    filteredViews.map((viewName) => {
                      const viewKey = `${selectedSchema.SCHEMA}.${viewName}`;
                      const isSelected = selectedView?.key === viewKey;
                      const viewData = viewDetails[viewKey];
                      const denied = isAccessDenied(selectedSchema.SCHEMA, viewName);

                      return (
                        <div
                          key={viewName}
                          onClick={() => {
                            if (denied) return;
                            loadViewDetails(selectedSchema.SCHEMA, viewName);
                          }}
                          title={denied ? 'Access denied by administrator' : undefined}
                          style={{
                            padding: '10px 14px',
                            borderBottom: '1px solid #f0f0f0',
                            cursor: denied ? 'not-allowed' : 'pointer',
                            opacity: denied ? 0.45 : 1,
                            backgroundColor: isSelected ? '#f0f5ff' : (denied ? '#fafafa' : 'transparent'),
                            borderLeft: isSelected ? '4px solid #1890ff' : '4px solid transparent',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => { if (!isSelected && !denied) e.currentTarget.style.backgroundColor = '#fafafa'; }}
                          onMouseLeave={(e) => { if (!isSelected && !denied) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          <Space direction="vertical" size={2} style={{ width: '100%' }}>
                            <Space>
                              <TableOutlined style={{ color: '#1890ff' }} />
                              <Text strong style={{ fontSize: 13 }}>{viewName}</Text>
                              {viewData?.COLUMNS && (
                                <Badge count={viewData.COLUMNS.length} style={{ backgroundColor: '#95de64' }} showZero />
                              )}
                              {denied && <Tag color="red" style={{ fontSize: 10 }}>No access</Tag>}
                            </Space>
                            {viewData?.VIEW_DESCRIPTION && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {viewData.VIEW_DESCRIPTION.length > 80 ? viewData.VIEW_DESCRIPTION.substring(0, 80) + '...' : viewData.VIEW_DESCRIPTION}
                              </Text>
                            )}
                          </Space>
                        </div>
                      );
                    })
                  )}
                </Card>
              </Col>

              {/* Column 3: Columns */}
              <Col xs={24} lg={8}>
                <Card
                  title={
                    <Space>
                      <ColumnHeightOutlined style={{ color: '#52c41a' }} />
                      <span>Columns</span>
                      {selectedView && viewDetails[selectedView.key] && (
                        <Badge count={filteredColumns.length} style={{ backgroundColor: '#52c41a' }} />
                      )}
                    </Space>
                  }
                  extra={
                    selectedView && (
                      <Search
                        placeholder="Search..."
                        size="small"
                        allowClear
                        value={columnSearch}
                        onChange={(e) => setColumnSearch(e.target.value)}
                        style={{ width: 160 }}
                      />
                    )
                  }
                  size="small"
                  style={{ height: 'calc(100vh - 460px)', minHeight: 300 }}
                  bodyStyle={{ height: 'calc(100% - 49px)', overflow: 'auto', padding: 0 }}
                >
                  {loadingView ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                      <Spin size="large" tip="Loading columns..." />
                    </div>
                  ) : !selectedView ? (
                    <Empty description="Select a view" style={{ padding: '40px 0' }} />
                  ) : filteredColumns.length === 0 ? (
                    <Empty description="No columns found" style={{ padding: '40px 0' }} />
                  ) : (
                    filteredColumns.map((col) => (
                      <div
                        key={col.COLUMN_NAME}
                        style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}
                      >
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Space>
                            <ColumnHeightOutlined style={{ color: '#52c41a' }} />
                            <Text strong style={{ color: '#52c41a', fontSize: 13 }}>{col.COLUMN_NAME}</Text>
                            <Tag color="blue" style={{ fontSize: 10 }}>{col.TYPE}</Tag>
                          </Space>
                          {col.COLUMN_DESCRIPTION && (
                            <Text type="secondary" style={{ fontSize: 11 }}>{col.COLUMN_DESCRIPTION}</Text>
                          )}
                        </Space>
                      </div>
                    ))
                  )}
                </Card>
              </Col>
            </Row>
          )}
        </Panel>
      </Collapse>
    </div>
  );
}

export default DataExplorer;
